import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { IconButton, Text, DefaultButton } from '@bingads-webui-react/fabric-control';
import { ImageContainer, TextItem } from '@bingads-webui-campaign-react/asset-library-creative-components';

import { getVideoSettingsEditorStyles } from './style';

export const VideoSettingsEditor = ({
  videoSettings,
  setVideoSettings,
  i18n,
  onLoadingLogo,
  onChangeLogo,
  newLogoDataObject,
  handleGenerate,
  disableGenerate,
  businessNameErrorMessage,
}) => {
  const businessName = useMemo(() => {
    if (videoSettings && videoSettings.updating && videoSettings.updating.businessName) {
      return videoSettings.updating.businessName.text;
    }

    return '';
  }, [videoSettings]);

  const currentLogo = useMemo(() => {
    if (videoSettings && videoSettings.updating && videoSettings.updating.logo) {
      return videoSettings.updating.logo.url;
    }

    return '';
  }, [videoSettings]);

  useEffect(() => {
    if (newLogoDataObject && newLogoDataObject.url) {
      setVideoSettings(prev => ({
        ...prev,
        updating: {
          ...prev.updating,
          logo: {
            url: newLogoDataObject.url,
            display: true,
          },
        },
      }));
    }
  }, [newLogoDataObject, setVideoSettings]);

  const onVideoSettingsChanged = useCallback((metaData) => {
    setVideoSettings({
      ...videoSettings,
      updating: {
        ...videoSettings.updating,
        ...metaData,
      },
    });
  }, [setVideoSettings, videoSettings]);

  const onBusinessNameChange = useCallback((str) => {
    onVideoSettingsChanged({
      businessName: {
        text: str,
        display: str !== '',
      },
    });
  }, [onVideoSettingsChanged]);

  const onEdit = useCallback(() => {
    // setDisplayEdit(false);
    onChangeLogo();
  }, [onChangeLogo]);

  const onDelete = useCallback(() => {
    onVideoSettingsChanged({
      logo: {
        url: '',
        display: false,
      },
    });
  }, [onVideoSettingsChanged]);

  const classes = useDefaultStyles(getVideoSettingsEditorStyles);

  return (
    <div className={classNames(classes.videoSettingsEditor, 'video-settings-editor')}>
      <TextItem
        title={i18n.getString(_TL_('Business Name'))}
        i18n={i18n}
        text={businessName}
        updateText={onBusinessNameChange}
        errorMessage={businessNameErrorMessage}
        ariaLabel={i18n.getString(_TL_('Business Name Text Input'))}
      />
      {
        currentLogo &&
        <ImageContainer
          onLoadingImage={onLoadingLogo}
          currentImage={currentLogo}
          title={i18n.getString(_TL_('Logo'))}
          i18n={i18n}
          displayEdit
          onEdit={onEdit}
          displayDelete
          onDelete={onDelete}
        />
      }
      {
        !currentLogo &&
        <div className="plus-button-container">
          <div className="item-title">
            <Text variant="smallPlus" >{i18n.getString(_TL_('Choose a logo'))}</Text>
          </div>
          <IconButton iconProps={{ iconName: 'Add' }} className={classNames('add-image-button')} onClick={onEdit} ariaLabel={i18n.getString(_TL_('Choose A Logo Button'))} />
        </div>
      }

      <DefaultButton
        styles={{ root: { marginTop: '24px' } }}
        iconProps={{ iconName: 'Send' }}
        onClick={handleGenerate}
        className="generate-video-button"
        text={i18n.getString(_TL_('Generate'))}
        disabled={disableGenerate}
      />
    </div>
  );
};

VideoSettingsEditor.propTypes = {
  videoSettings: PropTypes.shape({
    updating: PropTypes.shape({
      businessName: PropTypes.shape({
        text: PropTypes.string,
      }),
      logo: PropTypes.shape({
        url: PropTypes.string,
      }),
    }),
  }),
  setVideoSettings: PropTypes.func.isRequired,
  disableGenerate: PropTypes.bool.isRequired,
  handleGenerate: PropTypes.func.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  onLoadingLogo: PropTypes.func,
  onChangeLogo: PropTypes.func,
  newLogoDataObject: PropTypes.shape({
    url: PropTypes.string,
  }),
};

VideoSettingsEditor.defaultProps = {
  videoSettings: {},
  onLoadingLogo: () => {},
  onChangeLogo: () => {},
  newLogoDataObject: {},
};
